import React, { useState } from 'react';
import { X, Plus, RefreshCw, Check, Eye, EyeOff, Mail, UserPlus } from 'lucide-react';
import apiService from '../../services/api';

const PRIMARY = 'var(--accent)';
const PRIMARY_FAINT = 'var(--tint)';
const PRIMARY_BORDER = 'var(--accent)';

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  height: 36,
  padding: '0 12px',
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface-2)',
  fontSize: 13,
  color: 'var(--text)',
  outline: 'none',
};

const labelStyle = {
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--text-muted)',
  marginBottom: 4,
  display: 'block',
  letterSpacing: '0.03em',
};

function Field({ label, error, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={labelStyle}>{label}</label>
      {children}
      {error && <span style={{ fontSize: 11, color: 'var(--bad-text)' }}>{error}</span>}
    </div>
  );
}

function StepPill({ num, label, state }) {
  // state: 'active' | 'done' | 'idle'
  const bg = state === 'active' ? PRIMARY : state === 'done' ? PRIMARY_FAINT : 'var(--border-soft)';
  const color = state === 'active' ? 'var(--on-accent)' : state === 'done' ? PRIMARY : 'var(--text-muted)';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 7,
      padding: '6px 14px',
      borderRadius: 20,
      background: bg,
      transition: 'all 0.2s',
    }}>
      <div style={{
        width: 18, height: 18, borderRadius: '50%',
        background: state === 'active' ? 'var(--on-accent-soft)' : state === 'done' ? PRIMARY : 'var(--border)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 10, fontWeight: 700,
        color: state === 'active' ? 'var(--on-accent)' : state === 'done' ? 'var(--on-accent)' : 'var(--text-muted)',
        flexShrink: 0,
      }}>
        {state === 'done' ? <Check size={10} /> : num}
      </div>
      <span style={{ fontSize: 12, fontWeight: 600, color, whiteSpace: 'nowrap' }}>{label}</span>
    </div>
  );
}

export function AddUserModal({ onClose, onCreated, departments = [], currentUserName = 'Admin', companyName = 'Your Company' }) {
  const [step, setStep] = useState(1); // 1 = details, 2 = invite, 3 = success
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [inviteOpt, setInviteOpt] = useState('send'); // 'send' | 'skip'
  const [createdUser, setCreatedUser] = useState(null);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    password: '',
    role: 'member',
    departmentId: '',
  });
  const [errors, setErrors] = useState({});

  const set = (key) => (e) => {
    setForm(f => ({ ...f, [key]: e.target.value }));
    setErrors(er => ({ ...er, [key]: '' }));
  };

  function validate() {
    const errs = {};
    if (!form.firstName.trim()) errs.firstName = 'Required';
    if (!form.lastName.trim()) errs.lastName = 'Required';
    if (!form.email.trim() || !form.email.includes('@')) errs.email = 'Enter a valid email';
    if (form.password.length < 8) errs.password = 'Minimum 8 characters';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleCreateUser() {
    if (!validate()) return;
    setLoading(true);
    try {
      // 1. Create the user
      const res = await apiService.createUser({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        username: form.username.trim() || form.email.split('@')[0],
        password: form.password,
        role: form.role,
        departmentId: form.departmentId || null,
      });

      const newUser = res?.data?.user ?? res?.user ?? res;
      setCreatedUser(newUser);

      // 2. If a department was selected, also add as department member
      if (form.departmentId && newUser?.id) {
        try {
          await apiService.addDepartmentMember(form.departmentId, newUser.id);
        } catch (e) {
          console.warn('Dept member add failed (may already exist):', e);
        }
      }

      setStep(2);
    } catch (e) {
      const msg = e?.response?.data?.message ?? 'Failed to create user';
      setErrors(er => ({ ...er, _general: msg }));
    } finally {
      setLoading(false);
    }
  }

  async function handleFinish() {
    setLoading(true);
    try {
      if (inviteOpt === 'send' && createdUser?.id) {
        await apiService.sendUserInvite(createdUser.id);
      }
      setStep(3);
      // Notify parent to refresh list
      onCreated?.(createdUser);
      setTimeout(onClose, 2000);
    } catch (e) {
      console.error('Invite send failed:', e);
      // Still show success — user was created
      setStep(3);
      onCreated?.(createdUser);
      setTimeout(onClose, 2000);
    } finally {
      setLoading(false);
    }
  }

  const fullName = `${form.firstName} ${form.lastName}`.trim() || 'the new user';

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, zIndex: 50,
          background: 'var(--overlay)', backdropFilter: 'blur(4px)',
        }}
      />

      {/* Modal */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 51,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}>
        <div style={{
          background: 'var(--surface)',
          borderRadius: 20,
          width: '100%', maxWidth: 500,
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: 'var(--modal-shadow)',
          display: 'flex', flexDirection: 'column',
        }}>
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '20px 24px 16px',
            borderBottom: '1px solid var(--border-soft)',
            flexShrink: 0,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 34, height: 34, borderRadius: 10,
                background: PRIMARY_FAINT,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <UserPlus size={16} style={{ color: PRIMARY }} />
              </div>
              <div>
                <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', margin: 0 }}>
                  {step === 3 ? 'All done!' : 'Add new user'}
                </h2>
                <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0 }}>
                  {step === 1 && 'Create account & add to team'}
                  {step === 2 && 'Send welcome invite'}
                  {step === 3 && 'User created successfully'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                width: 30, height: 30, borderRadius: 8,
                border: '1px solid var(--border)', background: 'var(--surface)',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--text-muted)',
              }}
            >
              <X size={14} />
            </button>
          </div>

          {/* Step pills */}
          {step < 3 && (
            <div style={{
              display: 'flex', gap: 6, padding: '14px 24px',
              borderBottom: '1px solid var(--border-soft)',
              flexShrink: 0,
            }}>
              <StepPill num="1" label="Account details" state={step === 1 ? 'active' : 'done'} />
              <div style={{ width: 20, height: 1, background: 'var(--border)', alignSelf: 'center', flexShrink: 0 }} />
              <StepPill num="2" label="Send invite" state={step === 2 ? 'active' : step > 2 ? 'done' : 'idle'} />
            </div>
          )}

          {/* Body */}
          <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14, flex: 1 }}>

            {/* ── STEP 1 ── */}
            {step === 1 && (
              <>
                {errors._general && (
                  <div style={{
                    padding: '10px 14px', borderRadius: 10,
                    background: 'var(--bad-bg)', border: '1px solid var(--bad-border)',
                    fontSize: 13, color: 'var(--bad-text)',
                  }}>
                    {errors._general}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="First name *" error={errors.firstName}>
                    <input
                      autoFocus
                      style={{ ...inputStyle, borderColor: errors.firstName ? 'var(--bad-border)' : 'var(--border)' }}
                      placeholder="e.g. First Name"
                      value={form.firstName}
                      onChange={set('firstName')}
                    />
                  </Field>
                  <Field label="Last name *" error={errors.lastName}>
                    <input
                      style={{ ...inputStyle, borderColor: errors.lastName ? 'var(--bad-border)' : 'var(--border)' }}
                      placeholder="e.g. Last Name"
                      value={form.lastName}
                      onChange={set('lastName')}
                    />
                  </Field>
                </div>

                <Field label="Email address *" error={errors.email}>
                  <input
                    type="email"
                    style={{ ...inputStyle, borderColor: errors.email ? 'var(--bad-border)' : 'var(--border)' }}
                    placeholder="name@company.com"
                    value={form.email}
                    onChange={set('email')}
                  />
                </Field>

                <Field label="Username" error={errors.username}>
                  <input
                    style={inputStyle}
                    placeholder="Auto-generated from email if blank"
                    value={form.username}
                    onChange={set('username')}
                  />
                </Field>

                <Field label="Password *" error={errors.password}>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPw ? 'text' : 'password'}
                      style={{ ...inputStyle, paddingRight: 38, borderColor: errors.password ? 'var(--bad-border)' : 'var(--border)' }}
                      placeholder="Min 8 characters"
                      value={form.password}
                      onChange={set('password')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(s => !s)}
                      style={{
                        position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                        background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-faint)', padding: 0,
                        display: 'flex',
                      }}
                    >
                      {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </Field>

                <div style={{ height: 1, background: 'var(--border-soft)' }} />

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Role">
                    <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.role} onChange={set('role')}>
                      <option value="standard">Standard user</option>
                      <option value="admin">Director</option>
                      <option value="super_admin">Administrator</option>
                    </select>
                  </Field>
                  <Field label="Department">
                    <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.departmentId} onChange={set('departmentId')}>
                      <option value="">— None —</option>
                      {departments.map(d => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </Field>
                </div>
              </>
            )}

            {/* ── STEP 2 ── */}
            {step === 2 && (
              <>
                {/* Invite options */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    {
                      key: 'send',
                      icon: <Mail size={16} style={{ color: PRIMARY }} />,
                      label: 'Send invite email now',
                      desc: `${fullName} gets a welcome email with a link to activate their account`,
                    },
                    {
                      key: 'skip',
                      icon: <UserPlus size={16} style={{ color: 'var(--text-muted)' }} />,
                      label: 'Skip for now',
                      desc: 'Account is created — you can send the invite later from their profile',
                    },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      onClick={() => setInviteOpt(opt.key)}
                      style={{
                        display: 'flex', alignItems: 'flex-start', gap: 12,
                        padding: '12px 14px',
                        borderRadius: 12,
                        border: `1px solid ${inviteOpt === opt.key ? PRIMARY_BORDER : 'var(--border)'}`,
                        background: inviteOpt === opt.key ? PRIMARY_FAINT : 'var(--surface)',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{
                        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                        background: inviteOpt === opt.key ? 'var(--tint-strong)' : 'var(--border-soft)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {opt.icon}
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: '0 0 2px' }}>{opt.label}</p>
                        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>{opt.desc}</p>
                      </div>
                      <div style={{
                        width: 18, height: 18, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                        border: `2px solid ${inviteOpt === opt.key ? PRIMARY : 'var(--field-border)'}`,
                        background: inviteOpt === opt.key ? PRIMARY : 'var(--surface)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {inviteOpt === opt.key && <Check size={10} style={{ color: 'var(--on-accent)' }} strokeWidth={3} />}
                      </div>
                    </button>
                  ))}
                </div>

               
              </>
            )}

            {/* ── STEP 3 SUCCESS ── */}
            {step === 3 && (
              <div style={{ textAlign: 'center', padding: '12px 0 8px' }}>
                <div style={{
                  width: 56, height: 56, borderRadius: '50%',
                  background: 'rgba(22,163,74,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 16px',
                }}>
                  <Check size={26} color="#16a34a" strokeWidth={2.5} />
                </div>
                <p style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', marginBottom: 8 }}>
                  {inviteOpt === 'send' ? 'User created & invite sent!' : 'User created!'}
                </p>
                <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6 }}>
                  {inviteOpt === 'send'
                    ? `${fullName} will receive a welcome email shortly.`
                    : `${fullName}'s account is ready. You can send the invite from their profile.`
                  }
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          {step < 3 && (
            <div style={{
              display: 'flex', gap: 10, justifyContent: 'flex-end',
              padding: '16px 24px',
              borderTop: '1px solid var(--border-soft)',
              flexShrink: 0,
            }}>
              {step === 2 && (
                <button
                  onClick={() => setStep(1)}
                  style={{
                    padding: '8px 18px', borderRadius: 20,
                    border: '1px solid var(--border)', background: 'var(--surface)',
                    color: 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  ← Back
                </button>
              )}
              {step === 1 && (
                <button
                  onClick={onClose}
                  style={{
                    padding: '8px 18px', borderRadius: 20,
                    border: '1px solid var(--border)', background: 'var(--surface)',
                    color: 'var(--text-muted)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              )}
              <button
                onClick={step === 1 ? handleCreateUser : handleFinish}
                disabled={loading}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  padding: '8px 20px', borderRadius: 20,
                  background: loading ? '#E3DCCB' : '#FFC81E',
                  color: '#1C1A14', fontSize: 13, fontWeight: 600,
                  border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'background 0.15s',
                }}
              >
                {loading && <RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} />}
                {step === 1 && (loading ? 'Creating…' : 'Create user →')}
                {step === 2 && (
                  inviteOpt === 'send'
                    ? (loading ? 'Sending…' : <><Mail size={13} /> Send invite & finish</>)
                    : (loading ? 'Saving…' : 'Finish without invite')
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}